import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Shuffle, Share2, MessageCircle, ChevronLeft, ChevronRight, Volume2, Square, Menu, X } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { getCanon, getChapter } from "../api/client";

function groupBooks(books) {
  const g = { OT: [], NT: [], APO: [], ETH: [] };
  for (const b of books) {
    const cg = (b.canonGroup || "").toLowerCase();
    if (cg.includes("ethiop")) g.ETH.push(b);
    else if (cg.includes("deutero") || cg.includes("apocry")) g.APO.push(b);
    else if (b.testament === "NT") g.NT.push(b);
    else g.OT.push(b);
  }
  return g;
}

export default function Read() {
  const { lang } = useApp();
  const { bookId, chapter } = useParams();
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const mainRef = useRef(null);

  const curBook = bookId || "GEN";
  const curCh = Number(chapter) || 1;

  useEffect(() => { getCanon(lang).then((c) => setBooks(c.books || [])); }, [lang]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    window.speechSynthesis?.cancel();
    setSpeaking(false);
    getChapter(curBook, curCh, lang).then((d) => {
      if (active) { setData(d); setLoading(false); if (mainRef.current) mainRef.current.scrollTop = 0; }
    });
    return () => { active = false; };
  }, [curBook, curCh, lang]);

  const groups = useMemo(() => groupBooks(books), [books]);
  const bookMeta = books.find((b) => b.id === curBook);
  const maxCh = bookMeta?.numberOfChapters || curCh;

  const go = (id, ch) => { navigate(`/read/${id}/${ch}`); setSidebarOpen(false); };

  const openRandom = () => {
    if (!books.length) return;
    const b = books[Math.floor(Math.random() * books.length)];
    const ch = Math.floor(Math.random() * (b.numberOfChapters || 1)) + 1;
    go(b.id, ch);
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: "BibleVoice", url });
      else { await navigator.clipboard.writeText(url); toast.success(t(lang, "copied")); }
    } catch (e) {}
  };

  const listen = () => {
    if (!window.speechSynthesis || !data) return;
    if (speaking) { window.speechSynthesis.cancel(); setSpeaking(false); return; }
    const text = data.verses.map((v) => v.text).join(" ");
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang === "fr" ? "fr-FR" : lang === "es" ? "es-ES" : "en-US";
    u.rate = 0.92;
    u.onend = () => setSpeaking(false);
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  };

  const Group = ({ label, items }) =>
    items.length ? (
      <div className="mb-4">
        <p className="px-2 mb-1.5 text-[0.65rem] font-semibold tracking-[0.16em] uppercase text-[var(--gold-deep)]">{label}</p>
        {items.map((b) => (
          <div key={b.id}>
            <button
              onClick={() => go(b.id, 1)}
              className={`w-full text-left px-2.5 py-1.5 rounded-lg text-sm transition-colors ${
                b.id === curBook ? "bg-[rgba(243,195,73,0.28)] text-[var(--gold-deep)] font-semibold" : "text-[var(--ink-soft)] hover:bg-[rgba(243,195,73,0.14)]"
              }`}
            >
              {b.name}
            </button>
            {b.id === curBook && (
              <div className="grid grid-cols-6 gap-1 px-1.5 py-2">
                {Array.from({ length: b.numberOfChapters || 1 }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    onClick={() => go(b.id, n)}
                    className={`h-8 rounded-md text-xs font-medium transition-all ${
                      n === curCh ? "btn-gold" : "bg-[rgba(255,255,255,0.7)] text-[var(--ink-soft)] hover:bg-[rgba(255,240,200,0.9)] border border-[rgba(150,190,225,0.5)]"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    ) : null;

  return (
    <div className="relative z-10 min-h-screen flex flex-col">
      {/* Top bar */}
      <div className="sticky top-0 z-20 glass-strong border-b border-[rgba(210,170,80,0.3)] px-3 md:px-5 py-2.5 flex items-center gap-3">
        <button className="lg:hidden btn-ghost-gold rounded-full w-9 h-9 flex items-center justify-center" onClick={() => setSidebarOpen(true)}>
          <Menu size={18} />
        </button>
        <Logo size="sm" />
        <div className="flex-1 text-center font-display text-xl md:text-2xl font-semibold text-[var(--gold-deep)]">
          {data ? `${data.book.name} ${data.number}` : ""}
        </div>
        <div className="hidden md:block"><LanguageSwitcher variant="pills" /></div>
        <button onClick={openRandom} className="btn-ghost-gold rounded-full px-3 py-2 text-sm flex items-center gap-1.5"><Shuffle size={15} /><span className="hidden sm:inline">{t(lang, "random")}</span></button>
        <button onClick={share} className="btn-ghost-gold rounded-full px-3 py-2 text-sm flex items-center gap-1.5"><Share2 size={15} /><span className="hidden sm:inline">{t(lang, "share")}</span></button>
        <Link to="/guide" className="btn-gold rounded-full px-3 py-2 text-sm flex items-center gap-1.5"><MessageCircle size={15} /><span className="hidden sm:inline">{t(lang, "askAI")}</span></Link>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside className={`fixed lg:static inset-y-0 left-0 z-30 w-72 lg:w-80 shrink-0 glass-strong lg:bg-transparent lg:glass-none border-r border-[rgba(210,170,80,0.3)] overflow-y-auto p-4 transition-transform lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
          <div className="flex items-center justify-between lg:hidden mb-3">
            <span className="font-display text-lg font-semibold">Books</span>
            <button onClick={() => setSidebarOpen(false)}><X size={20} /></button>
          </div>
          <Group label={t(lang, "oldT")} items={groups.OT} />
          <Group label={t(lang, "newT")} items={groups.NT} />
          <Group label={t(lang, "apoc")} items={groups.APO} />
          <Group label={t(lang, "ethio")} items={groups.ETH} />
        </aside>
        {sidebarOpen && <div className="fixed inset-0 z-20 bg-black/20 lg:hidden" onClick={() => setSidebarOpen(false)} />}

        {/* Main scripture */}
        <main ref={mainRef} className="flex-1 min-w-0 overflow-y-auto px-5 md:px-10 lg:px-16 py-8 md:py-12">
          <div className="max-w-3xl mx-auto">
            {loading ? (
              <div className="space-y-4 animate-pulse">
                <div className="h-10 w-1/3 bg-[rgba(210,170,80,0.2)] rounded" />
                {Array.from({ length: 8 }).map((_, i) => (<div key={i} className="h-4 bg-[rgba(210,170,80,0.15)] rounded" style={{ width: `${70 + (i % 3) * 10}%` }} />))}
              </div>
            ) : data ? (
              <div className="fade-in">
                <h1 className="font-display text-5xl md:text-6xl font-semibold text-[var(--ink)]" style={{ textShadow: "0 1px 14px rgba(255,255,255,0.6)" }}>{data.book.name}</h1>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <p className="text-[var(--ink-soft)]" style={{ textShadow: "0 1px 10px rgba(255,255,255,0.55)" }}>{t(lang, "chapter")} {data.number} · {data.translation?.name}</p>
                  <button onClick={listen} className={`rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1.5 ${speaking ? "btn-gold" : "btn-ghost-gold"}`}>
                    {speaking ? <Square size={13} /> : <Volume2 size={14} />} {t(lang, "listen")}
                  </button>
                </div>
                <div className="mt-8 font-scripture text-[1.35rem] leading-[2.05rem] text-[var(--ink)]">
                  {data.verses.map((v) => (
                    <span key={v.number} className="align-baseline">
                      <sup className="text-[0.7rem] font-sans font-semibold text-[var(--gold-deep)] mr-1 align-super">{v.number}</sup>
                      {v.text}{" "}
                    </span>
                  ))}
                </div>
                <div className="mt-12 flex items-center justify-between gap-3">
                  <button disabled={curCh <= 1} onClick={() => go(curBook, curCh - 1)} className="btn-ghost-gold rounded-full px-5 py-2.5 flex items-center gap-2 disabled:opacity-40"><ChevronLeft size={16} /> {t(lang, "previous")}</button>
                  <button disabled={curCh >= maxCh} onClick={() => go(curBook, curCh + 1)} className="btn-gold rounded-full px-5 py-2.5 flex items-center gap-2 disabled:opacity-40">{t(lang, "next")} <ChevronRight size={16} /></button>
                </div>
              </div>
            ) : null}
          </div>
        </main>
      </div>
    </div>
  );
}
